// Guardar o compartir un video / imagen / audio que mandó el agente en el chat.
//
// Antes el chat pintaba un <video controls> pelón: en el iPhone no había forma
// práctica de guardar el material que el agente generaba para compartir
// (TKT-00217, «no puedo guardar el video», 24-oct). Aquí:
//   · Móvil con Web Share de archivos → menú nativo (iOS: «Guardar video» a
//     Fotos, WhatsApp, AirDrop…).
//   · Si no → descarga como archivo con su nombre.
//   · Si la descarga falla (CORS, red) → se abre en otra pestaña.
// El CDN (brainstream.b-cdn.net) responde Access-Control-Allow-Origin: *.

import { useState } from 'react';
import { Download, Loader2, Share2 } from 'lucide-react';

type Tipo = 'video' | 'imagen' | 'audio';

const EXT: Record<Tipo, string> = { video: 'mp4', imagen: 'jpg', audio: 'mp3' };

function nombreDesde(url: string, tipo: Tipo): string {
  try {
    const base = decodeURIComponent(new URL(url, window.location.href).pathname.split('/').pop() || '');
    if (base && /\.[a-z0-9]{2,5}$/i.test(base)) return base;
  } catch {
    /* URL rara: nombre genérico */
  }
  return `${tipo}-${Date.now()}.${EXT[tipo]}`;
}

function esMovil(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
}

export function MediaSave({ url, tipo }: { url: string; tipo: Tipo }) {
  const [estado, setEstado] = useState<'' | 'bajando' | 'error'>('');
  const movil = esMovil();
  const puedeCompartir = movil && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function guardar() {
    setEstado('bajando');
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const nombre = nombreDesde(url, tipo);
      const archivo = new File([blob], nombre, { type: blob.type || undefined });
      if (puedeCompartir && navigator.canShare?.({ files: [archivo] })) {
        try {
          await navigator.share({ files: [archivo], title: nombre });
        } catch (e) {
          // Cerrar el menú de compartir no es un error.
          if ((e as DOMException)?.name !== 'AbortError') throw e;
        }
      } else {
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = nombre;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(href), 30_000);
      }
      setEstado('');
    } catch {
      setEstado('error');
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  const Icono = puedeCompartir ? Share2 : Download;
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <button
        type="button"
        onClick={() => void guardar()}
        disabled={estado === 'bajando'}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
      >
        {estado === 'bajando' ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Icono size={14} aria-hidden />}
        {estado === 'bajando' ? 'Preparando…' : puedeCompartir ? 'Guardar o compartir' : 'Descargar'}
      </button>
      {estado === 'error' && (
        <span className="text-xs text-muted-foreground" role="status">No se pudo bajar; lo abrí en otra pestaña.</span>
      )}
    </div>
  );
}
